import React from "react";

/**
 * A tracking number's (lot's) unit cost, from its purchase order (2026-10-13, Mike).
 *
 * jobq-api stamps `cost_per_unit` on the lot at receipt from the PO line's price (a
 * transfer carries the source lot's cost; a customer's material has none). The list
 * and detail also return `po_line_cost_per_unit`, the PO line's price NOW: when the PO
 * was repriced after receipt (MTN 000527, PO 1389: received at $174.79, PO now
 * $177.99) both are shown, so the drift is visible instead of silent.
 */
const fmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export function formatUnitCost(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? fmt.format(n) : null;
}

/** What to show for one lot row: {cost, poNow, drift, isCustomer}. */
export function mtnUnitCost(row) {
  const isCustomer = row?.source_type === "customer_intake";
  const cost = formatUnitCost(row?.cost_per_unit);
  const lot = Number(row?.cost_per_unit);
  const line = Number(row?.po_line_cost_per_unit);
  const drift = !isCustomer && row?.po_line_cost_per_unit !== null && row?.po_line_cost_per_unit !== undefined
    && Number.isFinite(lot) && Number.isFinite(line) && Math.abs(lot - line) >= 0.005;
  return { cost, poNow: drift ? formatUnitCost(line) : null, drift, isCustomer };
}

/** Table cell content: "$577.66", a "PO now $X" note when it drifted, "—" otherwise. */
export default function MtnUnitCostCell({ row }) {
  const { cost, poNow, isCustomer } = mtnUnitCost(row);
  if (row?.source_type === "remnant") {
    // A remnant's own lot (2026-10-14) has no lot cost: the remnant is priced by its share of the sheet.
    return <span className="text-muted" title="A remnant's lot: priced as a remnant (its share of the sheet), not per lot">—</span>;
  }
  if (isCustomer) {
    return <span className="text-muted" title="The customer's own material: no cost to us">—</span>;
  }
  if (!cost) return <span className="text-muted">—</span>;
  return (
    <>
      <span>{cost}</span>
      {poNow && (
        <div className="small text-warning-emphasis" title="The PO was repriced after this lot was received">
          PO now {poNow}
        </div>
      )}
    </>
  );
}
